import { createContext, use } from "react";
import type { ApiError } from "@entrilia/shared/api/types.ts";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type { CurrentUser, ReportCompany, SharedReport } from "../ShareReport.types.ts";
import type {
  GroupBasedAuthorization,
  GroupForSharing,
  UserBasedAuthorization,
  UserForSharing,
} from "../../../../../../../../../api/types/svTypes.ts";
import type { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";

export interface SharingReportContextType {
  report: SharedReport;
  currentUser: CurrentUser;
  owner: UserBasedAuthorization | undefined;
  company: ReportCompany;
  sharedForCompanyAccess: ReportAccessType;
  isAuthorizationChanged: boolean;
  isSaving: boolean;
  saveError: ApiError | undefined;
  setSharedForCompanyAccess: (access: ReportAccessType) => void;
  authorizedUsers: UserBasedAuthorization[];
  setAuthorizedUsers: (users: UserBasedAuthorization[]) => void;
  onUserSelected: (users: UserForSharing[], access: ReportAccessType) => void;
  onUserAccessChanged: (userId: string, access: ReportAccessType) => void;
  onRemoveUser: (userId: string) => void;
  authorizedGroups: GroupBasedAuthorization[];
  onGroupSelected: (groups: GroupForSharing[], access: ReportAccessType) => void;
  onGroupAccessChanged: (groupId: string, access: ReportAccessType) => void;
  onRemoveGroup: (groupId: string) => void;
  // Whether the report belongs to a report group and can therefore inherit its sharing.
  canInherit: boolean;
  inheritFromParent: boolean;
  setInheritFromParent: (value: boolean) => void;
  onSave: () => void;
}

export const SharingReportContext = createContext<SharingReportContextType | undefined>(undefined);

export const useSharingReportContext = () => {
  const context = use(SharingReportContext);
  return defined(context);
};
